import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessAccounts,
  canAccessPortalBranding,
  END_USER_HIDDEN_NAV_IDS,
} from '../lib/accountRoles';
import {
  LayoutDashboard,
  Router,
  Users,
  CreditCard,
  Ticket,
  Headphones,
  UserCog,
  Palette,
} from 'lucide-react';

interface MobileNavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export const MobileNavigation = React.memo(function MobileNavigation({ 
  currentPage, 
  onPageChange 
}: MobileNavigationProps) {
  const { user } = useAuth();

  const baseNavItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard
    },
    {
      id: 'routers',
      label: 'Routers',
      icon: Router
    },
    {
      id: 'customers',
      label: 'Users',
      icon: Users
    },
    {
      id: 'service-plans',
      label: 'Service Plans',
      icon: CreditCard
    },
    {
      id: 'vouchers',
      label: 'Vouchers',
      icon: Ticket
    },
  ];

  const showAccountsNav = canAccessAccounts(user?.role, user?.parentAccountId);

  const portalBrandingItem = {
    id: 'portal-branding',
    label: 'Portal',
    icon: Palette,
  };
  const accountsItem = {
    id: 'accounts',
    label: 'Accounts',
    icon: UserCog,
  };

  const supportItem = {
    id: 'support-tickets',
    label: 'Support',
    icon: Headphones,
  };
  const showPortalBrandingNav = canAccessPortalBranding(user?.role, user?.parentAccountId);

  const navItems = [
    ...baseNavItems.filter((item) => {
      if (user?.role === 'end_user') {
        return !END_USER_HIDDEN_NAV_IDS.includes(item.id as (typeof END_USER_HIDDEN_NAV_IDS)[number]);
      }
      return true;
    }),
    ...(showPortalBrandingNav ? [portalBrandingItem] : []),
    supportItem,
    ...(showAccountsNav ? [accountsItem] : []),
  ];

  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-3 sm:gap-3 mb-4">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentPage === item.id;
        
        return (
          <button
            key={item.id}
            onClick={() => onPageChange(item.id)}
            className={`
              mobile-nav-item ignore-mobile-sizing h-[4.25rem] sm:h-16 w-full min-w-0
              flex flex-col items-center justify-center p-1.5 sm:p-2 rounded-lg
              transition-colors duration-200 border relative overflow-hidden
              ${isActive 
                ? 'text-white border-[var(--sw-brand)] shadow-md' 
                : 'text-muted-foreground border-border/50 hover:border-[var(--sw-brand)] hover:text-[var(--sw-brand)] hover:shadow-sm active:opacity-90'
              }
            `}
            style={isActive ? {
              backgroundColor: 'var(--sw-brand)'
            } : {}}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'var(--sw-brand-light)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = '';
              }
            }}
          >
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1 shrink-0" />
            <span className="text-[10px] sm:text-xs text-center leading-tight font-medium line-clamp-2 w-full px-0.5">
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
});